import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { Trial } from "@/types/trial";
import { format } from "date-fns";
import { Timestamp } from "firebase/firestore";
import { CalendarIcon } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import CropSelector from "@/components/CropSelector";
import LocationSelector from "@/components/LocationSelector";
import { useCrops, useLocations } from "@/hooks/useFirestore";
import { useAuth } from "@/contexts/AuthContext";
import { useDirtySnapshot } from "@/hooks/useDirtySnapshot";

interface NewTrialFormProps {
  onSubmit: (trial: Partial<Trial>) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  /** Reports whether the form has unsaved edits, so the parent dialog can guard against accidental close. */
  onDirtyChange?: (dirty: boolean) => void;
}

const NewTrialForm: React.FC<NewTrialFormProps> = ({ onSubmit, onCancel, isSubmitting = false, onDirtyChange }) => {
  const today = new Date();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { userRole } = useAuth();
  const { crops, loading: cropsLoading } = useCrops();
  const { locations, loading: locationsLoading } = useLocations();

  // Only editors and admins can add new crops/locations
  const canEdit = userRole && ["editor", "admin"].includes(userRole);

  const [trialData, setTrialData] = useState<Partial<Trial>>({
    name: "",
    crops: [],
    description: "",
    locationName: "",
    startDate: Timestamp.fromDate(today),
    endDate: null,
    hasMultipleLocations: false,
    locations: null,
  });

  // Surface unsaved-edit state so the wrapping dialog can confirm before discarding.
  const isDirty = useDirtySnapshot(trialData);
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = e.target.name;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let value: any = e.target.value;
    if (name === "startDate" || name === "endDate") {
      value = Timestamp.fromDate(new Date(e.target.value));
    }
    setTrialData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (!isFormValid()) {
      return;
    }

    const newTrial: Partial<Trial> = {
      id: `trial-${Date.now()}`,
      ...trialData,
    };

    onSubmit(newTrial);
  };

  const isFormValid = () => {
    return trialData.name && trialData.startDate;
  };

  const validationErrors = useMemo(() => {
    const errors: { field: string; message: string }[] = [];

    if (!trialData.name?.trim()) {
      errors.push({ field: "name", message: "Trial Name is required" });
    }

    if (!trialData.startDate) {
      errors.push({ field: "startDate", message: "Start Date is required" });
    }

    return errors;
  }, [trialData.name, trialData.startDate]);

  const hasFieldError = (fieldName: string) => {
    return hasSubmitted && validationErrors.some((e) => e.field === fieldName);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col h-full">
      <ScrollArea
        className="flex-1 max-h-[70vh]"
        viewportClassName="pl-1">
        <div className="space-y-4 pt-4 pr-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label
                htmlFor="name"
                className={hasFieldError("name") ? "text-destructive" : ""}>
                Trial Name{" "}
                <span className={hasFieldError("name") ? "text-destructive" : "text-muted-foreground"}>(required)</span>
              </Label>
              <Input
                id="name"
                name="name"
                value={trialData.name}
                onChange={handleChange}
                placeholder="Enter trial name"
                aria-invalid={hasFieldError("name")}
                aria-describedby={hasFieldError("name") ? "name-error" : undefined}
                className={hasFieldError("name") ? "border-destructive focus-visible:ring-destructive" : ""}
              />
              {hasFieldError("name") && (
                <p
                  id="name-error"
                  className="text-sm text-destructive">
                  Trial name is required
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="crops">Crops</Label>
              <CropSelector
                crops={crops}
                cropsLoading={cropsLoading}
                selectedCrops={trialData.crops || []}
                onToggleCrop={(cropName) => {
                  const currentCrops = trialData.crops || [];
                  const newCrops = currentCrops.includes(cropName)
                    ? currentCrops.filter((c) => c !== cropName)
                    : [...currentCrops, cropName];
                  setTrialData((prev) => ({ ...prev, crops: newCrops }));
                }}
                canAddCrop={canEdit}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              value={trialData.description || ""}
              onChange={handleChange}
              placeholder="Enter a brief description of the trial"
              rows={3}
            />
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="locationName">Location</Label>
              <LocationSelector
                locations={locations}
                locationsLoading={locationsLoading}
                selectedLocation={trialData.locationName || ""}
                onSelectLocation={(locationName) => setTrialData((prev) => ({ ...prev, locationName }))}
                canAddLocation={canEdit}
                placeholder="Select a location"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={hasFieldError("startDate") ? "text-destructive" : ""}>
                  Start Date{" "}
                  <span className={hasFieldError("startDate") ? "text-destructive" : "text-muted-foreground"}>
                    (required)
                  </span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      aria-invalid={hasFieldError("startDate")}
                      aria-describedby={hasFieldError("startDate") ? "startDate-error" : undefined}
                      className={cn(
                        "w-full pl-3 text-left font-normal",
                        !trialData.startDate && "text-muted-foreground",
                        hasFieldError("startDate") && "border-destructive focus-visible:ring-destructive"
                      )}>
                      {trialData.startDate ? format(trialData.startDate.toDate(), "PPP") : <span>Pick a date</span>}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-0"
                    align="start">
                    <Calendar
                      mode="single"
                      selected={trialData.startDate?.toDate()}
                      onSelect={(date) => {
                        if (date) {
                          setTrialData((prev) => ({ ...prev, startDate: Timestamp.fromDate(date) }));
                        }
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                {hasFieldError("startDate") && (
                  <p
                    id="startDate-error"
                    className="text-sm text-destructive">
                    Start date is required
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full pl-3 text-left font-normal",
                        !trialData.endDate && "text-muted-foreground"
                      )}>
                      {trialData.endDate ? format(trialData.endDate.toDate(), "PPP") : <span>Optional</span>}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-0"
                    align="start">
                    <Calendar
                      mode="single"
                      selected={trialData.endDate?.toDate() || undefined}
                      onSelect={(date) => {
                        setTrialData((prev) => ({ ...prev, endDate: date ? Timestamp.fromDate(date) : null }));
                      }}
                      disabled={(date) => (trialData.startDate ? date < trialData.startDate.toDate() : false)}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>

      <DialogFooter className="pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!isFormValid() || isSubmitting}>
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
              Creating...
            </span>
          ) : (
            "Create Trial"
          )}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default NewTrialForm;
